/**
 * Tipi CycloneDX (sottoinsieme usato dal portale) + diff fra due SBOM a livello di componente.
 *
 * Il diff è "stile git" ma semantico: confronta gli INSIEMI di componenti di due release
 * (aggiunti / rimossi / versione cambiata), non le righe del JSON. La chiave del componente è
 * indipendente dalla versione (group + name), così un cambio di versione risulta "modificato"
 * invece di rimosso+aggiunto.
 */

/** Componente CycloneDX (campi che ci interessano). */
export interface SbomComponent {
  name: string;
  version?: string;
  purl?: string;
  type?: string;
  group?: string;
}

/** Documento CycloneDX (sottoinsieme). */
export interface CycloneDxBom {
  bomFormat?: string;
  specVersion?: string;
  components?: SbomComponent[];
  metadata?: Record<string, unknown>;
}

/** Componente con versione cambiata fra due SBOM. */
export interface SbomComponentChange {
  key: string;
  name: string;
  group?: string;
  purl?: string;
  fromVersion?: string;
  toVersion?: string;
}

export interface SbomDiff {
  added: SbomComponent[];
  removed: SbomComponent[];
  changed: SbomComponentChange[];
  /** Componenti presenti in entrambe con la stessa versione. */
  unchangedCount: number;
}

/** Chiave indipendente dalla versione: identifica "lo stesso componente" fra release diverse. */
export function componentKey(c: SbomComponent): string {
  const base = c.group ? `${c.group}/${c.name}` : c.name;
  return base.toLowerCase();
}

/** Etichetta leggibile di un componente (group/name). */
export function componentLabel(c: SbomComponent): string {
  return c.group ? `${c.group}/${c.name}` : c.name;
}

function indexByKey(components: SbomComponent[]): Map<string, SbomComponent> {
  const map = new Map<string, SbomComponent>();
  for (const c of components) {
    // In caso di duplicati per chiave, l'ultimo vince (sufficiente per il confronto release).
    map.set(componentKey(c), c);
  }
  return map;
}

/**
 * Confronta i componenti di due SBOM CycloneDX.
 * @param from SBOM di partenza (release "vecchia").
 * @param to   SBOM di arrivo (release "nuova").
 */
export function diffSbomComponents(from: CycloneDxBom, to: CycloneDxBom): SbomDiff {
  const fromMap = indexByKey(from.components ?? []);
  const toMap = indexByKey(to.components ?? []);

  const added: SbomComponent[] = [];
  const removed: SbomComponent[] = [];
  const changed: SbomComponentChange[] = [];
  let unchangedCount = 0;

  // Presenti in `to`: nuovi o (eventualmente) con versione cambiata.
  for (const [key, toComp] of toMap) {
    const fromComp = fromMap.get(key);
    if (!fromComp) {
      added.push(toComp);
    } else if ((fromComp.version ?? '') !== (toComp.version ?? '')) {
      changed.push({
        key,
        name: toComp.name,
        ...(toComp.group !== undefined ? { group: toComp.group } : {}),
        ...(toComp.purl !== undefined ? { purl: toComp.purl } : {}),
        ...(fromComp.version !== undefined ? { fromVersion: fromComp.version } : {}),
        ...(toComp.version !== undefined ? { toVersion: toComp.version } : {}),
      });
    } else {
      unchangedCount += 1;
    }
  }

  // Presenti solo in `from`: rimossi.
  for (const [key, fromComp] of fromMap) {
    if (!toMap.has(key)) removed.push(fromComp);
  }

  const byLabel = (a: SbomComponent, b: SbomComponent) =>
    componentLabel(a).localeCompare(componentLabel(b));
  added.sort(byLabel);
  removed.sort(byLabel);
  changed.sort((a, b) => a.name.localeCompare(b.name));

  return { added, removed, changed, unchangedCount };
}
